// Stores
import { useResult } from "../../../../shared/formContext/hooks/useResult";

// Components
import Box from "../../../../components/Box/Box.js";
import IconContent from "../../../../components/IconContent/IconContent.js";
import Cta from "../../../../components/Cta/Cta";
import ShareFullLink from "../../../../shared/components/ShareFullLink";

// Views
import TableView from "./Views/TableView";
import ChartView from "./Views/ChartView";

// Utils
import { utils } from "../../../../helpers/index.js";

// WP react stuff
import { useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";

/** Which of the two equity scenarios the external API returns is shown in the top box. */
type EquityScenario = "prudente" | "ampliado";

const LABELS = {
    valueToConsider: __("Valor a considerar num", "df-simulators"),
    prudente: __("Cenário prudente", "df-simulators"),
    ampliado: __("Cenário ampliado", "df-simulators"),
    costes: __("Custos", "df-simulators"),
    ahorrosNecesarios: {
        prudente: __("Capitais próprios necessários (prudente)", "df-simulators"),
        ampliado: __("Capitais próprios necessários (ampliado)", "df-simulators"),
    },
};

/**
 * Inline "Valor a considerar num [select]" control rendered via the Box
 * header's `titleAlt` slot — same mechanism MAC's `ScenarioSelector` uses
 * for its "cenário recomendado/alternativo/limite" picker.
 */
const EquityScenarioSelector: React.FC<{
    scenario: EquityScenario;
    setScenario: (scenario: EquityScenario) => void;
}> = ({ scenario, setScenario }) => (
    <>
        {LABELS.valueToConsider}
        <select
            onChange={(e) => setScenario(e.target.value as EquityScenario)}
            value={scenario}
        >
            <option value="prudente">{LABELS.prudente}</option>
            <option value="ampliado">{LABELS.ampliado}</option>
        </select>
    </>
);

const QuotePayment: React.FC = () => {
    const { resultData } = useResult();
    const { response, loading } = resultData;
    const [scenario, setScenario] = useState<EquityScenario>("prudente");
    const [view, setView] = useState<"table" | "chart">("chart");

    const results = response?.data?.result?.results;

    return (
        <div className="quote-payment-simulator">
            {results && (
                <Box
                    grid
                    loading={loading}
                    backgroundColor={undefined}
                    className={undefined}
                    textColor={undefined}
                    vertical={undefined}
                    collapsible={undefined}
                    noPadding={undefined}
                    header={{
                        titleAlt: (
                            <EquityScenarioSelector
                                scenario={scenario}
                                setScenario={setScenario}
                            />
                        ),
                        info: utils.toEuro(results.paymentValue),
                        color: "orange-100",
                    }}
                >
                    <IconContent
                        title={LABELS.costes}
                        bigText={utils.toEuro(results.operatingCosts?.total)}
                        color="orange-100"
                        icon={""}
                        text={""}
                        textColor={""}
                        smallText={""}
                        className={""}
                    />
                    <IconContent
                        title={LABELS.ahorrosNecesarios[scenario]}
                        bigText={utils.toEuro(results.equityRequired?.[scenario]?.equityRequired)}
                        color="orange-100"
                        icon={""}
                        text={""}
                        textColor={""}
                        smallText={""}
                        className={""}
                    />
                </Box>
            )}

            {/* Share Simulation button */}
            <ShareFullLink />

            {results?.installments && (
                <Box
                    loading={loading}
                    grid={undefined}
                    backgroundColor={undefined}
                    className="chart-table"
                    vertical={undefined}
                    collapsible={undefined}
                    noPadding={undefined}
                    textColor={undefined}
                    header={{
                        title: __("Amortização", "df-simulators"),
                        textColor: "navy-100",
                        switcher: {
                            name: "quote-payment-amortizacao-view",
                            callback: (val: "table" | "chart") => setView(val),
                            fields: [
                                {
                                    label: __("Vista de tabela", "df-simulators"),
                                    value: "table",
                                },
                                {
                                    label: __("Vista de gráfico", "df-simulators"),
                                    value: "chart",
                                    default: true,
                                },
                            ],
                        },
                    }}
                >
                    {view === "table" && <TableView />}
                    {view === "chart" && <ChartView />}
                </Box>
            )}

            {/* Call-to-Action - Standard project pattern */}
            <Cta />
        </div>
    );
};

export default QuotePayment;
